import type { InvestorProfile } from '@/api/custom';
import {
  createSessionAssociation,
  deleteSessionAssociationRecord,
  getSessionAssociationByAdvisorId,
  getSessionAssociationByThreadId,
  updateSessionAssociationRecord,
} from '@/api/weave-x';
import { patchThreadMetadata } from '@/api/weave-x-agents';
import type { Thread } from './types';

export type SessionAssignment = NonNullable<
  Awaited<ReturnType<typeof getSessionAssociationByThreadId>>
>;

type AssignmentListener = (threadId: string, assignment: SessionAssignment | null) => void;
const assignmentListeners = new Set<AssignmentListener>();

export function subscribeToSessionAssignments(listener: AssignmentListener): () => void {
  assignmentListeners.add(listener);
  return () => {
    assignmentListeners.delete(listener);
  };
}

function publishSavedAssignment(
  threadId: string,
  assignment: SessionAssignment | null,
): SessionAssignment | null {
  // A save may outlive the component that started it after a session switch.
  for (const listener of assignmentListeners) {
    try {
      listener(threadId, assignment);
    } catch (error) {
      console.error('Failed to display saved session assignment:', { threadId, error });
    }
  }
  return assignment;
}

export async function loadFirmDataSessionAssignments(
  threads: Thread[],
  advisorId: string,
): Promise<Thread[]> {
  const assignments = new Map<string, SessionAssignment>();
  let nextToken = '';
  do {
    const page = await getSessionAssociationByAdvisorId(advisorId, 100, nextToken);
    for (const assignment of page.items) {
      const current = assignments.get(assignment.threadId);
      if (!current || assignment.updatedAt > current.updatedAt) {
        assignments.set(assignment.threadId, assignment);
      }
    }
    nextToken = page.nextToken || '';
  } while (nextToken);

  return threads.map((thread) => {
    const assignment = assignments.get(thread.thread_id);
    // Keep older metadata-only labels until the advisor changes that assignment.
    return assignment ? threadWithSessionAssignment(thread, assignment) : thread;
  });
}

export function threadWithSessionAssignment(
  thread: Thread,
  assignment: SessionAssignment | null,
): Thread {
  return {
    ...thread,
    metadata: { ...thread.metadata, investorId: assignment?.investorId ?? null },
  };
}

export async function saveFirmDataSessionAssignment({
  thread,
  investor,
  title,
  currentAssignment,
}: {
  thread: Pick<Thread, 'thread_id' | 'metadata'>;
  investor: Pick<InvestorProfile, 'id' | 'cognito_id'> | null;
  title: string;
  currentAssignment?: SessionAssignment | null;
}): Promise<SessionAssignment | null> {
  const existing =
    currentAssignment === undefined
      ? await getSessionAssociationByThreadId(thread.thread_id)
      : currentAssignment;

  if (!investor) {
    // The caller may have hidden unavailable labels. Persist the clear first so
    // deleting the association cannot revive a legacy label on the next reload.
    await patchThreadMetadata(thread.thread_id, { investorId: null });
    if (existing) await deleteSessionAssociationRecord(existing.assignmentId);
    return publishSavedAssignment(thread.thread_id, null);
  }

  if (existing?.investorId === investor.id) {
    const assignment = await updateSessionAssociationRecord(existing.assignmentId, {
      title,
      cognitoId: investor.cognito_id || undefined,
    });
    return publishSavedAssignment(thread.thread_id, assignment);
  }

  const replacement = await createSessionAssociation({
    investorId: investor.id,
    cognitoId: investor.cognito_id || undefined,
    threadId: thread.thread_id,
    title,
    description: existing?.description ?? '',
    notes: existing?.notes ?? '',
  });
  if (existing) {
    try {
      await deleteSessionAssociationRecord(existing.assignmentId);
    } catch (error) {
      try {
        await deleteSessionAssociationRecord(replacement.assignmentId);
      } catch (rollbackError) {
        console.error('Failed to roll back session assignment:', rollbackError);
      }
      throw error;
    }
  }
  return publishSavedAssignment(thread.thread_id, replacement);
}
